/* 三國逗陣打 · 存檔合併（v0.2.1 雲端存檔）
 * 同一個帳號在兩個地方各有進度（雲端那份、這台裝置第一次登入前玩的）時，合併成一份：
 *   只會增加的數值（軍功、場數、勝場、熟練度、最佳紀錄）取大；擁有的東西（造型、成就、提示）取聯集；
 *   錢（銅錢、黃金）與增益卡、儲值紀錄以雲端為準——不然兩台裝置各存一筆錢，一合併就變兩倍；
 *   戰役紀錄合併後依時間排序，留最近 30 場（同 meta.ts）。純函式，不碰 DOM／localStorage。 */

type Obj = Record<string, any>;
const num = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : 0);
const maxMap = (a: Obj = {}, b: Obj = {}) => { const o: Obj = Object.assign({}, a); for (const k in b) o[k] = Math.max(num(o[k]), num(b[k])); return o; };
const union = (a: Obj = {}, b: Obj = {}) => Object.assign({}, b, a); // 兩邊有的都留；同一個 key 以 a（雲端）為準
const HERO_KEYS = ['xp', 'games', 'wins', 'k', 'd', 'a', 'best'];

export function mergeProfiles(cloud: Obj, local: Obj): Obj {
  if (!cloud) return Object.assign({}, local);
  if (!local) return Object.assign({}, cloud);
  const out: Obj = Object.assign({}, local, cloud); // 沒特別處理的欄位：以雲端為準
  for (const k of ['xp', 'games', 'wins', 'bestStreak']) out[k] = Math.max(num(cloud[k]), num(local[k]));
  out.winStreak = num(cloud.winStreak);
  // 各武將熟練度：每個欄位取大
  const heroes: Obj = {};
  for (const id of new Set([...Object.keys(cloud.heroes || {}), ...Object.keys(local.heroes || {})])) {
    const c = (cloud.heroes || {})[id] || {}, l = (local.heroes || {})[id] || {}, h: Obj = Object.assign({}, l, c);
    for (const k of HERO_KEYS) h[k] = Math.max(num(c[k]), num(l[k]));
    heroes[id] = h;
  }
  out.heroes = heroes;
  out.factionsWon = maxMap(cloud.factionsWon, local.factionsWon);
  out.best = maxMap(cloud.best, local.best);
  out.ach = union(cloud.ach, local.ach);
  out.tips = union(cloud.tips, local.tips);
  out.owned = union(cloud.owned, local.owned);
  out.skin = union(cloud.skin, local.skin);
  // 登入天數：取大；最後登入日取較晚的
  const cl = cloud.login || {}, ll = local.login || {};
  out.login = { days: Math.max(num(cl.days), num(ll.days)), last: [cl.last, ll.last].filter(Boolean).sort().pop() || null };
  // 每日軍令：同一天的取進度多的那份；不同天取較新的
  const cd = cloud.daily, ld = local.daily;
  const prog = (d: Obj) => (d.q || []).reduce((s: number, q: Obj) => s + num(q.n) + (q.done ? 1e6 : 0), 0);
  out.daily = !cd ? ld || null : !ld ? cd : cd.date !== ld.date ? (cd.date > ld.date ? cd : ld) : (prog(ld) > prog(cd) ? ld : cd);
  // 戰役紀錄（meta.ts：d 日期、at 結束時間、hero、k/dd/a、sc 分數、t 對戰秒數）：合併、去重、最新在前、留 30 場
  const seen = new Set<string>(), hist: Obj[] = [];
  for (const h of [...(cloud.history || []), ...(local.history || [])]) {
    const key = JSON.stringify([h.d, h.at || 0, h.hero, h.k, h.dd, h.a, h.sc, h.t]);
    if (seen.has(key)) continue; seen.add(key); hist.push(h);
  }
  const when = (h: Obj) => num(h.at) || Date.parse(h.d || '') || 0; // 舊紀錄沒有 at：用日期
  hist.sort((a, b) => when(b) - when(a));
  out.history = hist.slice(0, 30);
  // 錢、增益卡、儲值紀錄：以雲端為準
  for (const k of ['copper', 'gold', 'boost', 'topups']) if (k in cloud) out[k] = cloud[k];
  return out;
}
